<template>
  <div class="qb-external-filters">
    <label class="text-lg text-marine">{{$t('external_filters')}}</label>
    <div class="card p10">
      <div
        id="external-filters"
        :class="{ 'category-drop-off': true, 'dragging': dragging }"
        @dragover.prevent
        @drop.prevent="onDropFilters"
        >
        <app-tag v-for="(mention, i) in filters" :key="mention+i"
            parentContainerId="qb-filters"
            :tag="mention"
            @deleteMentionTag="deleteMentionTag"
            @toggleSettings="toggleSettings"
          />
        <div v-if="dragging && filters.length === 0" class="placeholder">{{$t('drop_here')}}</div>
      </div>
    </div>

    <app-form-modal
      v-if="settingsModalTag"
      id="external-filter-settings-form"
      title="external_filters_settings.title"
      :subtitle="formSubtitle"
      :model="model"
      :fieldsObjects="settingsForm"
      :loading="categoryModalLoading"
      @closeModal="onClose"
      @onSave="onSave"
    />
  </div>
</template>

<script>
  import { mapActions, mapState } from 'vuex';
  import Tag from './tag';
  import FormModal from '../form-modal';
  import { QUERIES } from '../../config/const';

  export default {
    props: {
      filters: {
        type: Array,
        default: () => []
      },
      dragging: {
        type: Boolean,
        default: () => false
      }
    },
    components: {
      'app-tag': Tag,
      'app-form-modal': FormModal
    },
    data: function() {
      return {
        QUERIES,
        settingsModalTag: null,
        model: {
          hidden_tag_ids: []
        },
        loadingSettings: false,
        hidden_tag_options: [],
        isSearchable: false,
        hiddenTagTds: [],
        customClass: '',
        // Page size for `getCategory` paginated tag requests (category / tag external filters). 
        hiddenTagPageSize: 100,
        hiddenTagHasMore: true,
        hiddenTagsPagingLocked: false,
        /** Last pagination object from `getCategory` (paginated tags list). */
        categoryTagPagination: {},
        // Shown at bottom of multiselect while loading the next category page (silent global busy).
        categoryOptionsLoadingMore: false
      }
    },
    computed: {
      ...mapState({
        loading: state => state.queryBuilder.category.category.isBusy,
        isBreakdownBusy: state => state.queryBuilder?.standardBreakdown?.standardBreakdown?.isBusy,
        queryId: state => state.queryBuilder?.query?.query?.data?.id
      }),
      projectId () {
        return this.$route.params.project_id;
      },
      /**
       * Full-modal loader: for category/tag settings, hide overlay once first options exist so pagination can update silently.
       * @returns {boolean}
       */
      categoryModalLoading () {
        const smt = this.settingsModalTag;
        if (!smt) {
          return this.loading;
        }
        if (smt.type === 'category' || smt.type === 'tag') {
          return this.loading && !this.hidden_tag_options.length;
        }
        return this.loading;
      },
      formSubtitle () {
        return this.settingsModalTag.type === 'tag' ? this.settingsModalTag.category_name + ': ' + this.settingsModalTag.tag_name : this.settingsModalTag.name;
      },
      settingsForm () {
        const hiddenTagIdsField = {
          type: 'select',
          id: 'hidden_tag_ids',
          name: 'hidden_tag_ids',
          customClass: this.customClass,
          label: this.$t('external_filters_settings.hidden_tag_ids.title'),
          multiple: true,
          searchable: this.isSearchable,
          hideSelected: true,
          value: this.hiddenTagTds,
          options: this.hidden_tag_options,
          translate_label_tooltip: this.$t('external_filters_settings.hidden_tag_ids.text'),
          loading: this.isBreakdownBusy,
          update: (val)=> this.update(val),
          onSearchChange: (val) => this.onSearchChange(val),
          clearSelectValue: (val) => this.clearSelectValue(val),
          selectAllAvailableOptions: (val) => this.selectAllAvailableOptions(val),
          selectAllTooltipMessage: this.$t('form.tooltip.select_all'),
          clearAllTooltipMessage: this.$t('form.tooltip.clear_selection')
        };
        const smt = this.settingsModalTag;
        if (smt && (smt.type === 'category' || smt.type === 'tag')) {
          hiddenTagIdsField.onOptionsScrollEnd = () => this.loadMoreCategoryHiddenTags();
          hiddenTagIdsField.optionsFooterLoading = this.categoryOptionsLoadingMore;
        }
        return {
          hidden_tag_ids: hiddenTagIdsField
        };
      }
    },
    methods: {
      ...mapActions({
        removeExternalFilter: 'queryBuilder/query/removeExternalFilter',
        addExternalFilter: 'queryBuilder/query/addExternalFilter',
        getCategory: 'queryBuilder/category/getCategory',
        getHiddenTags: 'queryBuilder/category/getHiddenTags',
        getStandardBreakdown: 'queryBuilder/standardBreakdown/getStandardBreakdown',
      }),
      onDropFilters (e) {
        if(!this.dragging) {
          return;
        }

        const data = e.dataTransfer.getData('filters');
        if (data) {
          const tag = JSON.parse(data).filter(dt => dt.type === QUERIES.FILTER_TYPE.TAG || dt.type === QUERIES.FILTER_TYPE.BREAKDOWN || dt.type === QUERIES.FILTER_TYPE.CATEGORY)[0];

          if (tag) this.addExternalFilter(tag);
        }
      },
      deleteMentionTag (tag) {
        const tagId = tag.tag_id ? tag.tag_id : tag.id;
        this.removeExternalFilter(tagId);
      },
      toggleSettings (tag) {
        this.settingsModalTag = tag;
        this.customClass = '';
      },
      onSave (model) {
        const tag = { ...this.settingsModalTag, settings: model }
        this.addExternalFilter(tag);
        this.onClose();
      },
      onClose () {
        this.settingsModalTag = null;
        this.hidden_tag_options = [];
      },
      update (val) {
        this.hiddenTagTds = val.hidden_tag_ids;
        this.customClass = val.hidden_tag_ids.length && this.isSearchable ? 'override_inline_style' : ''
      },
      clearSelectValue(val) {
        this.customClass = ''
      },
      selectAllAvailableOptions(val) {
        if(this.isSearchable){
          this.customClass = 'override_inline_style';
        }
      },
      onSearchChange (value) {
        if (value != "") {
          const breakdownId = this.settingsModalTag.breakdown_id;
          this.getStandardBreakdown({ projectId: this.projectId, breakdownId, pagination: {perPage: this.hiddenTagTds.length + 15, query: value } }).then(r => {
          let options = Object.values([
            ...this.hidden_tag_options,
            ...r.data.sub_options.filter(t => this.settingsModalTag.id !== t.id)
            .map(t => ({ value: t.id, label: t.name }))]
            .reduce((acc, cur) => {
              acc[cur.value] = cur;
              return acc;
            }, {}));
            this.hidden_tag_options = options;
          });
        }
      },
      /**
       * Fetches one page of tags from `GET /projects/:id/queries/categories/:category_id` (see `getCategory` action).
       * @param {Object} opts
       * @param {boolean} [opts.append] When true, merges tags into `hidden_tag_options`.
       * @param {Object} opts.pagination Pagination + optional `sub_category_id` query params.
       * @returns {Promise<void>}
       */
      fetchCategoryHiddenTagsPage (opts = {}) {
        const { append = false, pagination } = opts;
        const val = this.settingsModalTag;
        if (!val || (val.type !== 'tag' && val.type !== 'category') || !pagination) {
          return Promise.resolve();
        }
        const categoryId = val.type === 'tag' ? val.category_id : val.id;
        if (append) {
          this.categoryOptionsLoadingMore = true;
        }
        return this.getCategory({
          projectId: this.projectId,
          categoryId,
          skipCommit: true,
          silent: append,
          pagination: { ...pagination, query_id: this.queryId }
        }).then((categoryRes) => {
          const tags = categoryRes?.data?.tags || [];
          const rows = tags
            .filter((t) => t.id && (val.type === 'tag' ? t.id !== val.tag_id : val.type === 'category'))
            .map((t) => ({ value: t.id, label: t.name }));
          if (append) {
            this.hidden_tag_options = this.hidden_tag_options.concat(rows);
          } else {
            this.hidden_tag_options = rows;
          }
          const pg = categoryRes?.pagination || {};
          this.categoryTagPagination = { ...pagination, ...pg };
          const cp = pg.currentPage ?? pagination.currentPage;
          const tp = pg.totalPages ?? 1;
          this.hiddenTagHasMore = tp > 0 && cp < tp;
        }).finally(() => {
          if (append) {
            this.categoryOptionsLoadingMore = false;
          }
        });
      },
      /**
       * Loads the next page of category tags when the multiselect list is scrolled to the end.
       */
      loadMoreCategoryHiddenTags () {
        const val = this.settingsModalTag;
        if (!val || (val.type !== 'tag' && val.type !== 'category')) {
          return;
        }
        if (!this.hiddenTagHasMore || this.hiddenTagsPagingLocked || this.categoryOptionsLoadingMore) {
          return;
        }
        const p = this.categoryTagPagination || {};
        const currentPage = p.currentPage ?? 1;
        const totalPages = p.totalPages ?? 1;
        if (currentPage >= totalPages) {
          return;
        }
        const nextPage = currentPage + 1;
        const pagination = {
          ...p,
          query_id: this.queryId,
          perPage: p.perPage || this.hiddenTagPageSize,
          currentPage: nextPage
        };
        if (val.sub_category_id) {
          pagination.sub_category_id = val.sub_category_id;
        }
        this.hiddenTagsPagingLocked = true;
        this.fetchCategoryHiddenTagsPage({ append: true, pagination }).finally(() => {
          this.hiddenTagsPagingLocked = false;
        });
      },
    },
    watch: {
      'settingsModalTag': function (val) {
          if (!val) {
            this.hiddenTagsPagingLocked = false;
            this.categoryTagPagination = {};
            this.categoryOptionsLoadingMore = false;
            return;
          }
          if (val.type === 'breakdown') {
            this.categoryTagPagination = {};
            this.isSearchable = QUERIES.ALLOW_EXTERNAL_FILTERS_SEARCHABLE.indexOf(val.breakdown_id) !== -1;
            this.hiddenTagTds = val.settings && val.settings.hidden_tag_ids || [];
            const skipLimitIds = QUERIES.ALLOW_EXTERNAL_FILTERS_SKIP_LIMIT;
            this.getStandardBreakdown({ projectId: this.projectId, breakdownId: val.breakdown_id, pagination: {perPage: this.hiddenTagTds.length + 40, skipLimit: skipLimitIds.includes(val.breakdown_id) } }).then(r => {
            let options = Object.values([
              ...(val?.selected_options || []), 
              ...r.data.sub_options.filter(t => val.id !== t.id)
              .map(t => ({ value: t.id, label: t.name }))]
              .reduce((acc, cur) => {
                acc[cur.value] = cur;
                return acc;
              }, {}));
              this.hidden_tag_options = options?.filter(option => option.value !== val.id);
              if(this.hiddenTagTds.length && this.isSearchable) {
                this.customClass = 'override_inline_style'
              }
            });
          } else {
            const categoryId = val.type === 'tag' ? val.category_id : val.id;
            const pagination = {
              query_id: this.queryId,
              perPage: this.hiddenTagPageSize,
              currentPage: 1
            };
            if (val.sub_category_id) pagination.sub_category_id = val.sub_category_id;

            Promise.all([
              this.fetchCategoryHiddenTagsPage({ append: false, pagination }),
              this.getHiddenTags({ projectId: this.projectId, queryId: this.queryId, categoryId })
            ]).then(([, hiddenTagIds]) => {
              const ids = Array.isArray(hiddenTagIds) ? hiddenTagIds : [];
              this.hiddenTagTds = ids;
              this.model.hidden_tag_ids = ids;
            });
        }
      }
    }
  }
</script>

<style lang="scss">
  @import "../../styles/components/coding-queries/external-filters";
</style>